<template>
  <div class="batch-upload">

    <!-- Upload tab -->
    <div v-if="!results">
      <div
        class="batch-drop-zone"
        :class="{ 'batch-drop-zone--over': isDragOver }"
        @dragover.prevent="isDragOver = true"
        @dragleave="isDragOver = false"
        @drop.prevent="handleDrop"
        @click="$refs.fileInput.click()"
      >
        <input
          ref="fileInput"
          type="file"
          multiple
          accept=".wav,.flac,audio/wav,audio/flac,audio/x-wav"
          style="display:none"
          @change="handleFileInput"
        />
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M9 18V5l12-2v13" stroke="#9ca3af" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          <circle cx="6" cy="18" r="3" stroke="#9ca3af" stroke-width="1.5"/>
          <circle cx="18" cy="16" r="3" stroke="#9ca3af" stroke-width="1.5"/>
        </svg>
        <p class="batch-drop-zone__label">WAV / FLAC 파일을 드래그하거나 클릭하여 선택</p>
        <p class="batch-drop-zone__hint">단일 디스크: 001_제목 · 멀티 디스크: 1-01_제목, 1_01_제목, 1.1_제목</p>
      </div>

      <div v-if="pendingFiles.length" class="preview-wrap">

        <div v-if="uploading" ref="uploadConsole" class="upload-console">
          <div class="upload-console__topline">
            <span class="upload-console__cursor" aria-hidden="true">▋</span>
            <span class="upload-console__label">파일 전송 중</span>
            <span class="upload-console__fraction">{{ completedCount }}&thinsp;/&thinsp;{{ matchedCount }}</span>
          </div>
          <div class="upload-console__track">
            <div class="upload-console__fill" :style="{ width: overallProgress + '%' }">
              <span class="upload-console__shimmer"></span>
            </div>
          </div>
          <div class="upload-console__meta">
            <span class="upload-console__pct">{{ overallProgress }}%</span>
            <span class="upload-console__status">완료까지 업로드 진행 중</span>
          </div>
        </div>

        <table class="batch-table">
          <thead>
            <tr>
              <th>파일명</th>
              <th>#</th>
              <th>매칭된 트랙</th>
              <th class="col-progress">진행률</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="f in pendingFiles"
              :key="f.file.name"
              :class="{ 'row--unmatched': !f.track }"
            >
              <td class="col-filename">{{ f.file.name }}</td>
              <td>{{ f.trackLabel || '—' }}</td>
              <td>
                <span v-if="f.track" class="match-badge match-badge--ok">{{ f.track.name }}</span>
                <span v-else class="match-badge match-badge--none">매칭 없음</span>
              </td>
              <td class="col-progress">
                <div
                  v-if="f.track && (uploading || (fileProgress[f.file.name] !== undefined))"
                  class="file-progress"
                  :class="{ 'file-progress--done': (fileProgress[f.file.name] || 0) >= 100 }"
                >
                  <div class="file-progress__track">
                    <div class="file-progress__bar" :style="{ width: `${fileProgress[f.file.name] || 0}%` }">
                      <span
                        v-if="(fileProgress[f.file.name] || 0) > 0 && (fileProgress[f.file.name] || 0) < 100"
                        class="file-progress__sweep"
                      ></span>
                    </div>
                  </div>
                  <span class="file-progress__pct">
                    {{ (fileProgress[f.file.name] || 0) >= 100 ? '✓' : `${fileProgress[f.file.name] || 0}%` }}
                  </span>
                </div>
                <span v-else class="file-progress__placeholder">—</span>
              </td>
            </tr>
          </tbody>
        </table>

        <div v-if="overLimit" class="unmatched-warn">
          ⚠ 트랙 수보다 많은 파일이 선택되었습니다. 처음 {{ totalTracks }}개 파일만 처리됩니다.
        </div>
        <div v-if="unmatchedBeforeUpload > 0" class="unmatched-warn">
          ⚠ {{ unmatchedBeforeUpload }}개 파일은 매칭된 트랙이 없어 건너뜁니다.
        </div>

        <div class="batch-footer">
          <vs-button color="danger" type="filled" :disabled="uploading" @click="clearFiles">초기화</vs-button>
          <vs-button
            type="filled"
            :disabled="matchedCount === 0 || uploading"
            @click="uploadFiles"
          >
            <span v-if="uploading">업로드 중...</span>
            <span v-else>{{ matchedCount }}개 트랙에 파일 업로드</span>
          </vs-button>
        </div>
      </div>
    </div>

    <!-- Results -->
    <div v-if="results" class="results-wrap">
      <div class="results-summary">
        <span class="result-pill result-pill--ok">✓ {{ successCount }} 첨부 완료</span>
        <span v-if="unmatchedCount" class="result-pill result-pill--warn">— {{ unmatchedCount }} 매칭 없음</span>
        <span v-if="failCount" class="result-pill result-pill--fail">✗ {{ failCount }} 오류</span>
      </div>

      <table class="batch-table">
        <thead>
          <tr>
            <th>파일명</th>
            <th>트랙</th>
            <th>결과</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="r in results" :key="r.filename">
            <td class="col-filename">{{ r.filename }}</td>
            <td>{{ r.track_name || '—' }}</td>
            <td>
              <span class="status-badge" :class="`status-badge--${r.status}`">
                {{ statusLabel(r.status) }}
              </span>
            </td>
          </tr>
        </tbody>
      </table>

      <div class="batch-footer">
        <vs-button type="filled" @click="finish">완료</vs-button>
      </div>
    </div>

  </div>
</template>

<script>
export default {
  name: 'TrackFileBatchUpload',

  props: {
    tracks: {
      type: Array,
      default: () => [],
    },
    albumId: {
      type: [Number, String],
      required: true,
    },
  },

  data() {
    return {
      isDragOver: false,
      pendingFiles: [],
      uploading: false,
      fileProgress: {},
      overLimit: false,
      results: null,
    }
  },

  computed: {
    trackMap() {
      const map = {}
      for (let v = 0; v < this.tracks.length; v++) {
        const disk = this.tracks[v]
        for (let t = 0; t < disk.length; t++) {
          const track = disk[t]
          const volume = track.volume || (v + 1)
          const tracknr = track.tracknr || (t + 1)
          map[`${volume}-${tracknr}`] = track
        }
      }
      return map
    },
    totalTracks() {
      return Object.keys(this.trackMap).length
    },
    matchedCount() {
      return this.pendingFiles.filter(f => f.track).length
    },
    overallProgress() {
      const matched = this.pendingFiles.filter(f => f.track)
      if (!matched.length) return 0
      const sum = matched.reduce((acc, f) => acc + (this.fileProgress[f.file.name] || 0), 0)
      return Math.round(sum / matched.length)
    },
    completedCount() {
      return this.pendingFiles.filter(f => f.track && (this.fileProgress[f.file.name] || 0) >= 100).length
    },
    unmatchedBeforeUpload() {
      return this.pendingFiles.filter(f => !f.track).length
    },
    successCount() {
      return (this.results || []).filter(r => r.status === 'attached').length
    },
    unmatchedCount() {
      return (this.results || []).filter(r => r.status === 'unmatched').length
    },
    failCount() {
      return (this.results || []).filter(r => r.status === 'error').length
    },
  },

  methods: {
    extractTrackKey(filename) {
      const multi = filename.match(/^(\d+)[-_.](\d+)/)
      if (multi) {
        const volume = parseInt(multi[1], 10)
        const tracknr = parseInt(multi[2], 10)
        return { key: `${volume}-${tracknr}`, label: `${volume}-${tracknr}` }
      }
      const single = parseInt(filename.slice(0, 3), 10)
      if (!isNaN(single)) {
        return { key: `1-${single}`, label: String(single) }
      }
      return null
    },

    matchFile(file) {
      const parsed = this.extractTrackKey(file.name)
      const track = parsed ? (this.trackMap[parsed.key] || null) : null
      return { file, trackLabel: parsed ? parsed.label : null, track }
    },

    validateFiles(files) {
      const valid = files.filter(f =>
        f.type === 'audio/wav' ||
        f.type === 'audio/flac' ||
        f.type === 'audio/x-wav' ||
        /\.(wav|flac)$/i.test(f.name)
      )
      const limit = Math.max(this.totalTracks, 1)
      this.overLimit = valid.length > limit
      return valid.slice(0, limit)
    },

    handleDrop(event) {
      this.isDragOver = false
      this.addFiles(Array.from(event.dataTransfer ? event.dataTransfer.files : []))
    },

    handleFileInput(event) {
      const input = event.target
      this.addFiles(Array.from(input.files || []))
      input.value = ''
    },

    addFiles(files) {
      this.pendingFiles = this.validateFiles(files).map(f => this.matchFile(f))
    },

    clearFiles() {
      this.pendingFiles = []
      this.fileProgress = {}
      this.overLimit = false
    },

    async uploadOneFile(pf) {
      const fallback = {
        filename: pf.file.name,
        track_name: pf.track ? pf.track.name : null,
        status: 'error',
      }
      try {
        const data = await this.$store.dispatch('POST_ATTACH_TRACK_FILE', {
          album_id: this.albumId,
          file: pf.file,
          track_id: pf.track.id,
          onProgress: (pct) => {
            this.$set(this.fileProgress, pf.file.name, pct)
          },
        })
        const result = data && data.results && data.results[0]
        return result || fallback
      } catch (err) {
        return fallback
      }
    },

    async uploadFiles() {
      this.uploading = true
      this.fileProgress = {}
      await this.$nextTick()
      if (this.$refs.uploadConsole) {
        this.$refs.uploadConsole.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
      }
      try {
        const matched = this.pendingFiles.filter(pf => pf.track)
        const CONCURRENCY = 5
        const apiResults = new Array(matched.length)
        const queue = matched.map((pf, i) => ({ pf, i }))

        const worker = async () => {
          while (queue.length > 0) {
            const item = queue.shift()
            if (!item) break
            apiResults[item.i] = await this.uploadOneFile(item.pf)
          }
        }
        await Promise.all(
          Array.from({ length: Math.min(CONCURRENCY, matched.length) }, () => worker())
        )

        const unmatched = this.pendingFiles
          .filter(pf => !pf.track)
          .map(pf => ({ filename: pf.file.name, track_name: null, status: 'unmatched' }))

        this.results = [...apiResults.filter(r => r != null), ...unmatched]
      } finally {
        this.uploading = false
      }
    },

    statusLabel(status) {
      if (status === 'attached') return '완료'
      if (status === 'unmatched') return '매칭 없음'
      return '오류'
    },

    finish() {
      this.$emit('done')
      this.results = null
      this.pendingFiles = []
      this.fileProgress = {}
      this.closeModal('trackFileBatch')
    },
  },
}
</script>

<style lang="scss" scoped>
.batch-upload {
  padding: 4px 0;
}

.batch-drop-zone {
  border: 2px dashed #d1d5db;
  border-radius: 10px;
  padding: 36px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;

  &:hover, &--over {
    border-color: #6b7280;
    background: #f9fafb;
  }

  &__label {
    font-size: 13px;
    color: #374151;
    font-weight: 500;
    margin: 0;
  }

  &__hint {
    font-size: 12px;
    color: #9ca3af;
    margin: 0;
    text-align: center;
  }
}

.preview-wrap {
  margin-top: 16px;
}

.batch-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  margin-bottom: 4px;

  th {
    text-align: left;
    padding: 6px 10px;
    font-weight: 600;
    color: #374151;
    border-bottom: 1px solid #e5e7eb;
    white-space: nowrap;
  }

  td {
    padding: 7px 10px;
    color: #374151;
    border-bottom: 1px solid #f3f4f6;
    vertical-align: middle;
  }

  .col-filename {
    max-width: 320px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: #6b7280;
    font-size: 12px;
  }

  .col-progress {
    width: 180px;
    min-width: 160px;
  }

  .row--unmatched td { color: #9ca3af; }
}

.file-progress {
  display: flex;
  align-items: center;
  gap: 6px;

  &__track {
    flex: 1;
    height: 4px;
    background: #e5e7eb;
    border-radius: 9999px;
    overflow: hidden;
  }

  &__bar {
    height: 100%;
    background: #1081E5;
    border-radius: 9999px;
    transition: width 0.2s ease-out;
    position: relative;
    overflow: hidden;
  }

  &__sweep {
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg,
      transparent 0%,
      rgba(255, 255, 255, 0.6) 50%,
      transparent 100%
    );
    animation: sweep 1s ease-in-out infinite;
  }

  &__pct {
    font-size: 11px;
    font-variant-numeric: tabular-nums;
    color: #6b7280;
    min-width: 28px;
    text-align: right;
    transition: color 0.15s;
  }

  &__placeholder {
    font-size: 11px;
    color: #d1d5db;
  }

  &--done &__bar { background: #16a34a; }
  &--done &__pct { color: #16a34a; font-weight: 600; }
}

.match-badge, .status-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 9999px;
  font-size: 12px;
  font-weight: 500;
}

.match-badge {
  &--ok   { background: #f0fdf4; color: #16a34a; border: 1px solid #bbf7d0; }
  &--none { background: #f9fafb; color: #9ca3af; border: 1px solid #e5e7eb; }
}

.status-badge {
  &--attached  { background: #f0fdf4; color: #16a34a; border: 1px solid #bbf7d0; }
  &--unmatched { background: #f9fafb; color: #9ca3af; border: 1px solid #e5e7eb; }
  &--error     { background: #fef2f2; color: #dc2626; border: 1px solid #fecaca; }
}

.upload-console {
  background: #ffffff;
  border: 1px solid #e2e4e8;
  border-radius: 8px;
  padding: 14px 16px 12px;
  margin-bottom: 14px;

  &__topline {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 10px;
  }

  &__cursor {
    color: #1081E5;
    font-size: 14px;
    line-height: 1;
    animation: cursor-blink 1s step-end infinite;
  }

  &__label {
    flex: 1;
    font-size: 10.5px;
    font-weight: 700;
    letter-spacing: 0.12em;
    color: #1081E5;
    text-transform: uppercase;
  }

  &__fraction {
    font-size: 12px;
    font-variant-numeric: tabular-nums;
    color: #9ca3af;
  }

  &__track {
    height: 5px;
    background: #eef0f4;
    border-radius: 3px;
    overflow: hidden;
    position: relative;
    margin-bottom: 8px;
  }

  &__fill {
    height: 100%;
    background: #1081E5;
    border-radius: 3px;
    position: relative;
    overflow: hidden;
    transition: width 0.25s ease-out;
  }

  &__shimmer {
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg,
      transparent 0%,
      rgba(255, 255, 255, 0.0) 35%,
      rgba(255, 255, 255, 0.55) 50%,
      rgba(255, 255, 255, 0.0) 65%,
      transparent 100%
    );
    animation: sweep 1.4s ease-in-out infinite;
  }

  &__meta {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }

  &__pct {
    font-size: 11px;
    font-variant-numeric: tabular-nums;
    font-weight: 700;
    color: #1081E5;
  }

  &__status {
    font-size: 11px;
    color: #9ca3af;
    letter-spacing: 0.02em;
  }
}

@keyframes sweep {
  0%   { transform: translateX(-200%); }
  100% { transform: translateX(300%); }
}

@keyframes cursor-blink {
  0%, 100% { opacity: 1; }
  50%       { opacity: 0; }
}

.unmatched-warn {
  margin-top: 12px;
  padding: 8px 12px;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 6px;
  font-size: 12px;
  color: #d97706;
}

.batch-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 16px;
}

.results-wrap {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.results-summary {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.result-pill {
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 9999px;
  font-size: 13px;
  font-weight: 500;

  &--ok   { background: #f0fdf4; color: #16a34a; border: 1px solid #bbf7d0; }
  &--warn { background: #fffbeb; color: #d97706; border: 1px solid #fde68a; }
  &--fail { background: #fef2f2; color: #dc2626; border: 1px solid #fecaca; }
}

// ── Dark mode overrides ──────────────────────────────────────
body.theme-dark {
  .batch-drop-zone {
    border-color: rgba(255, 255, 255, 0.18);
    background: rgba(255, 255, 255, 0.02);

    &:hover, &--over {
      border-color: rgba(255, 255, 255, 0.4);
      background: rgba(255, 255, 255, 0.06);
    }

    &__label { color: #e5e7eb; }
    &__hint  { color: #9ca3af; }
  }

  .batch-table {
    th {
      color: #e5e7eb;
      border-bottom-color: rgba(255, 255, 255, 0.12);
    }
    td {
      color: #e5e7eb;
      border-bottom-color: rgba(255, 255, 255, 0.06);
    }
    .col-filename { color: #9ca3af; }
    .row--unmatched td { color: #6b7280; }
  }

  .match-badge {
    &--ok   { background: rgba(34, 197, 94, 0.15);  color: #4ade80; border-color: rgba(34, 197, 94, 0.35); }
    &--none { background: rgba(255, 255, 255, 0.06); color: #9ca3af; border-color: rgba(255, 255, 255, 0.12); }
  }

  .status-badge {
    &--attached  { background: rgba(34, 197, 94, 0.15);  color: #4ade80; border-color: rgba(34, 197, 94, 0.35); }
    &--unmatched { background: rgba(255, 255, 255, 0.06); color: #9ca3af; border-color: rgba(255, 255, 255, 0.12); }
    &--error     { background: rgba(239, 68, 68, 0.15);  color: #f87171; border-color: rgba(239, 68, 68, 0.35); }
  }

  .upload-console {
    background: rgba(255, 255, 255, 0.04);
    border-color: rgba(255, 255, 255, 0.12);

    &__cursor, &__label, &__pct { color: #60a5fa; }
    &__fill { background: #60a5fa; }
    &__track { background: rgba(255, 255, 255, 0.08); }
    &__fraction, &__status { color: #9ca3af; }
  }

  .file-progress {
    &__track { background: rgba(255, 255, 255, 0.1); }
    &__bar { background: #60a5fa; }
    &__pct { color: #9ca3af; }
    &__placeholder { color: rgba(255, 255, 255, 0.18); }
    &--done &__bar { background: #4ade80; }
    &--done &__pct { color: #4ade80; }
  }

  .unmatched-warn {
    background: rgba(245, 158, 11, 0.12);
    border-color: rgba(245, 158, 11, 0.35);
    color: #fbbf24;
  }

  .result-pill {
    &--ok   { background: rgba(34, 197, 94, 0.15);  color: #4ade80; border-color: rgba(34, 197, 94, 0.35); }
    &--warn { background: rgba(245, 158, 11, 0.15); color: #fbbf24; border-color: rgba(245, 158, 11, 0.35); }
    &--fail { background: rgba(239, 68, 68, 0.15);  color: #f87171; border-color: rgba(239, 68, 68, 0.35); }
  }
}
</style>
